import type { TStoreName as _TStoreName } from "../types/domain/store";
import { buildDocument } from "./build-document";
import { APP_NAME, STORE_SUFFIX } from "./store";

/** 스토어별로 다른 쿼리만 키로 두고, 나머지는 default 에 `${S}` 보간으로 한 벌만 유지한다. */
const withFragments = (operations: Partial<Record<_TStoreName, string>> & { default: string }) =>
  buildDocument(operations[APP_NAME] ?? operations.default);

const S = STORE_SUFFIX;

export const productDetailQuery = withFragments({
  default: `#graphql
    query ProductDetail${S}($handle: String!) {
      product(handle: $handle) {
        ...ProductDetailFields${S}
      }
    }
  `,
});

export const productDetailRecommendQuery = withFragments({
  default: `#graphql
    query ProductRecommendations${S}($productId: ID!, $intent: ProductRecommendationIntent) {
      productRecommendations(productId: $productId, intent: $intent) {
        ...ProductRecommendFields${S}
      }
    }
  `,
});

export const productVariantQuery = withFragments({
  default: `#graphql
    query ProductVariant${S}($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          ...VariantBaseFields
          product {
            ...ProductCommonFields
            launch: metafield(namespace: "custom", key: "launch") {
              ...MetafieldFields
              reference {
                ...MetaobjectImageFields
              }
            }
          }
          dropDate: metafield(namespace: "operation", key: "drop_date") {
            ...MetafieldFields
          }
          launch: metafield(namespace: "custom", key: "launch") {
            ...MetafieldFields
            reference {
              ...MetaobjectImageFields
            }
          }
          seongsuStock: metafield(namespace: "custom", key: "seongsu_stock") {
            ...MetafieldFields
          }
          yeonhuiStock: metafield(namespace: "custom", key: "yeonhui_stock") {
            ...MetafieldFields
          }
        }
      }
    }
  `,
  tune: `#graphql
    query ProductVariantTune($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          ...VariantBaseFields
          product {
            ...ProductFieldsTune
            draw: metafield(namespace: "custom", key: "draw") {
              ...MetafieldFields
              reference {
                ... on Metaobject {
                  id
                  type
                  fields {
                    key
                    type
                    value
                    reference {
                      ...MediaImageFields
                      ...VideoImageFields
                    }
                    references(first: 10) {
                      nodes {
                        ...MediaImageFields
                        ...VideoImageFields
                        ... on Metaobject {
                          id
                          type
                          fields {
                            key
                            value
                            reference {
                              ...MediaImageFields
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
          dropDate: metafield(namespace: "operation", key: "drop_date") {
            ...MetafieldFields
          }
          seongsuStock: metafield(namespace: "custom", key: "seongsu_stock") {
            ...MetafieldFields
          }
          yeonhuiStock: metafield(namespace: "custom", key: "yeonhui_stock") {
            ...MetafieldFields
          }
        }
      }
    }
  `,
  wellbeingexpress: `#graphql
    query ProductVariantWellbeingexpress($id: ID!) {
      node(id: $id) {
        ... on ProductVariant {
          ...VariantBaseFields
          product {
            ...ProductFieldsWellbeingexpress
            launch: metafield(namespace: "custom", key: "launch") {
              ...MetafieldFields
              reference {
                ...MetaobjectImageFields
              }
            }
          }
          dropDate: metafield(namespace: "operation", key: "drop_date") {
            ...MetafieldFields
          }
          launch: metafield(namespace: "custom", key: "launch") {
            ...MetafieldFields
            reference {
              ...MetaobjectImageFields
            }
          }
          seongsuStock: metafield(namespace: "custom", key: "seongsu_stock") {
            ...MetafieldFields
          }
          yeonhuiStock: metafield(namespace: "custom", key: "yeonhui_stock") {
            ...MetafieldFields
          }
        }
      }
    }
  `,
});

export const productsQuery = withFragments({
  default: `#graphql
    query Products${S}($first: Int!, $query: String, $reverse: Boolean, $sortKey: ProductSortKeys) {
      products(first: $first, query: $query, reverse: $reverse, sortKey: $sortKey) {
        edges {
          node {
            ...CollectionDetailProductFields${S}
          }
        }
      }
    }
  `,
});

/**
 * 상품 GID 목록으로 조회
 */
export const productsByIdsQuery = withFragments({
  default: `#graphql
    query ProductsByIds${S}($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          ...ProductCommonFields
          ...ProductMetafields${S}
          variants(first: 40) {
            edges {
              node {
                ...VariantBaseFields
                ...VariantCommonMetafields
                product { ...ProductCommonFields }
              }
            }
          }
        }
      }
    }
  `,
});

/**
 * 상품 GID 목록으로 태그만 조회 — CAA 라인엔 상품 태그가 없어 프리오더 판별(hasPreorderCaa)용으로 따로 조회
 */
export const productTagsByIdsQuery = `#graphql
  query ProductTagsByIds($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Product {
        id
        tags
      }
    }
  }
`;
